import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';

import ActivateDefaultResponse from 'actions/composition/activate_default_response';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemType from 'models/conversation_item_type';
import CreateConversation from 'actions/conversation/create_conversation';
import { getActiveChatSessionIdFromHistory } from 'actions/communication_session/session_finder';
import { getLatestManualItem, isCustomerItem } from 'scripts/application/lib/conversation_history_helpers';
import i18n from 'scripts/i18n/i18n';
import { MessageChannelType, ModerationStatus } from 'models/conversation_message';
import registerHotkey from 'components/hotkeys/register_hotkey';
import withShortcuts from 'scripts/presentation/decorators/keypress_shortcut_decorator';

export class ResponsePrompt extends React.Component {
  constructor(props) {
    super(props);

    _.bindAll(this, ['handlePromptClick']);
  }

  render() {
    return (
      <div
        className={classnames('responsePrompt', { 'responsePrompt-disabled': this.props.isDisabled })}
        data-aid="defaultResponsePrompt"
        onClick={this.handlePromptClick}
      >
        {this.getDisplayText()}
      </div>
    );
  }

  getDisplayText() {
    const { isNewConversationPending, hasConversations, customerHasActiveChat, latestCustomerItem } = this.props;

    if (isNewConversationPending) {
      return i18n.t('Creating a new conversation...');
    }

    if (!hasConversations) {
      return i18n.t('Start a conversation');
    }

    const { contentType, moderationStatus, isAutomation } = getItemAttributes(latestCustomerItem);

    if (customerHasActiveChat || (isAutomation && contentType === ConversationItemType.CHAT)) {
      return i18n.t('Reply with a chat');
    }

    if (contentType === MessageChannelType.INSTAGRAM_COMMENT) {
      if (moderationStatus === ModerationStatus.HIDDEN || moderationStatus === ModerationStatus.DELETED) {
        return i18n.t('Create a note');
      }
      return i18n.t('Reply via Instagram Comment');
    }

    return getPromptForContentType(contentType);
  }

  handlePromptClick() {
    this.respond();
  }

  respond() {
    if (this.props.isDisabled) {
      return;
    }

    if (!this.props.hasConversations) {
      this.props.onCreate();
      return;
    }

    this.props.onReply();
  }
}

// Literal i18n.t() calls (rather than a key table) so i18next-parser can see
// every phrase. Instagram Comment is handled separately due to moderation.
function getPromptForContentType(contentType) {
  switch (contentType) {
    case ConversationItemType.EMAIL:
      return i18n.t('Reply with an email');
    case ConversationItemType.FB_MESSAGE_INCOMING:
      return i18n.t('Reply via Facebook');
    case ConversationItemType.VOICEMAIL:
      return i18n.t('Reply with a call');
    case ConversationItemType.SMS:
      return i18n.t('Reply with an SMS');
    case MessageChannelType.TWITTER:
      return i18n.t('Reply with a tweet');
    case MessageChannelType.WHATSAPP:
      return i18n.t('Reply with a WhatsApp');
    case MessageChannelType.INSTAGRAM_DIRECT:
      return i18n.t('Reply via Instagram');
    default:
      return i18n.t('Create a note');
  }
}

function getItemAttributes(item) {
  if (!item) {
    return { contentType: undefined, moderationStatus: undefined, isAutomation: false };
  }

  const type = item.contentType();
  const content = item.content;
  const isAutomation = type === ConversationItemType.MESSAGE_AUTOMATION_SUMMARY;

  let contentType = type;
  if (type === ConversationItemType.CONVERSATION_MESSAGE) {
    contentType = content?.channel;
  } else if (isAutomation) {
    contentType = content?.interactionType;
  }

  const moderationStatus = content?.meta?.moderationStatus;
  return { contentType, moderationStatus, isAutomation };
}

export const ResponsePromptWithShortcutHandler = withShortcuts(ResponsePrompt, [
  registerHotkey({
    key: 'alt+r',
    callback: instance => instance.respond(),
    group: 'Conversation',
    label: 'Reply to or reopen conversation',
  }),
]);

ResponsePrompt.propTypes = ResponsePromptWithShortcutHandler.propTypes = {
  hasConversations: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isNewConversationPending: PropTypes.bool,
  latestCustomerItem: PropTypes.instanceOf(ConversationItem),
  onCreate: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

const ResponsePromptContainer = connect(mapStateToProps, mapExecuteToProps)(ResponsePromptWithShortcutHandler);
export default ResponsePromptContainer;

ResponsePromptContainer.propTypes = {
  conversationId: PropTypes.string,
};

function mapStateToProps({ getProvider }, { conversationId }) {
  const profile = getProvider('profile').get();
  let conversationHistory = getProvider('conversationHistory');
  let conversations = getProvider('conversations');

  let latestCustomerItem = getLatestManualItem({
    conversationHistory,
    conversationId,
    filter: isCustomerItem,
  });

  let isNewConversationPending = getProvider('conversations').isPendingNew();
  let currentItemsLoading = getProvider('conversationHistory').isLoading();
  const isNavigatingToNextConversation = getProvider('conversationWorkflow').get().isNavigatingNextConversation;

  return {
    customerHasActiveChat: !!getActiveChatSessionIdFromHistory({ conversationHistory, conversations }),
    hasConversations: conversations.count() > 0,
    isDisabled:
      !(profile && profile.id) || isNewConversationPending || currentItemsLoading || isNavigatingToNextConversation,
    isNewConversationPending,
    latestCustomerItem,
  };
}

function mapExecuteToProps(executeAction, { conversationId }) {
  return {
    onCreate: () => executeAction(CreateConversation),
    onReply: () => executeAction(ActivateDefaultResponse, { conversationId }),
  };
}
